/**
 * The visit address (O10, F5.md V): `?visit=<object>:<id>[/<job>][,<next>...]`.
 *
 * A level names one record and, after `/`, either the job it opens
 * (`person:p2/access`) or a second record the object needs (`enroll:p3/c1`).
 * Levels join with commas into the trail; the last level is the visit drawn,
 * the earlier ones are the way back. The address grants nothing: every level
 * resolves through the person's own reads.
 */

import type { VisitAddress } from "@/components/ui/box.schema";

export type { VisitAddress };

export interface VisitLevel {
  object: string;
  id: string;
  /** The job the level opens, or the second record its object needs. */
  job: string | null;
}

/** Deeper than this is not a trail a person walked, so it is refused whole. */
export const MAX_TRAIL = 8;

const LEVEL = /^([a-z][a-z-]*):([A-Za-z0-9_-]+)(?:\/([A-Za-z0-9_-]+))?$/;

/** One level, or null when it isn't one. A null level is refused and names nothing (R1). */
export function parseLevel(raw: string): VisitLevel | null {
  const match = LEVEL.exec(raw);
  if (!match) return null;
  return { object: match[1], id: match[2], job: match[3] ?? null };
}

export function formatLevel(level: VisitLevel): VisitAddress {
  return `${level.object}:${level.id}${level.job ? `/${level.job}` : ""}`;
}

export function isVisitAddress(raw: string): raw is VisitAddress {
  return parseLevel(raw) !== null;
}

/**
 * The trail as written, last level last. Levels stay raw so a malformed one
 * still draws the refusal in its place instead of silently opening its parent.
 */
export function readTrail(search: string | URLSearchParams): string[] {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const raw = params.get("visit");
  if (!raw) return [];
  return raw.split(",").map((level) => level.trim()).filter(Boolean);
}

/**
 * The search string with `visit` set to the trail, or removed when the trail is
 * empty. Every other parameter is the origin's own and is kept as it was. The
 * address stays readable in the bar (`?visit=person:p3,enroll:p3/c1`).
 */
export function searchWithTrail(search: string, trail: readonly string[]): string {
  const params = new URLSearchParams(search);
  params.delete("visit");
  const rest = params.toString();
  const visit = trail
    .map((level) => encodeURIComponent(level).replace(/%3A/gi, ":").replace(/%2F/gi, "/"))
    .join(",");
  const query = [rest, visit && `visit=${visit}`].filter(Boolean).join("&");
  return query ? `?${query}` : "";
}
